import {
  Scale, Sparkles, Wand2, Radio, Store, BellOff, Camera, Link2, Bot, Layers, LayoutGrid,
} from "lucide-react";

export const FEATURES = [
  {
    slug: "compare-prices",
    group: "Shop",
    title: "Compare prices",
    blurb: "Best offer across stores",
    icon: Scale,
    kicker: "Shop",
    headline: "See the offer before you buy.",
    lede: "Search once. Shoptosa lines up partner listings and marketplace sellers so the price, store, and buy link sit on the same card.",
    points: [
      "A search returns live partner cards, including eBay when that catalog answers.",
      "Each card keeps its own price, photo, and store. The buy button opens that store.",
      "Affiliate links are labeled. A purchase through one can earn Shoptosa a commission at no extra cost to you.",
    ],
    openTo: "/shop",
    openLabel: "Open the shop",
    registerRole: "buyer",
  },
  {
    slug: "ai-search",
    group: "Shop",
    title: "AI search",
    blurb: "Ask — get matching items",
    icon: Sparkles,
    kicker: "Shop",
    headline: "Describe it. We find the listing.",
    lede: "Type the product the way you would say it. Search matches titles across the shop and the partner catalogs that are turned on.",
    points: [
      "A plain phrase is enough: a brand, a color, a product.",
      "Results stay tied to the store that sells them.",
      "Category filters narrow the grid after the search returns.",
    ],
    openTo: "/shop",
    openLabel: "Search the shop",
    registerRole: "buyer",
  },
  {
    slug: "virtual-try-on",
    group: "Shop",
    title: "Virtual try-on",
    blurb: "Preview on you",
    icon: Wand2,
    kicker: "Shop",
    headline: "Preview the piece on you.",
    lede: "Upload a photo and place an item on it before you buy. Try-on opens after you sign in, so the photo stays on your account.",
    points: [
      "Sign up or log in, then open try-on from this page.",
      "Your photo is used for the preview on that item.",
      "You still buy from the store that sells it.",
    ],
    openTo: "/tryon",
    openLabel: "Open try-on",
    registerRole: "buyer",
  },
  {
    slug: "live-selling",
    group: "Shop",
    title: "Live selling",
    blurb: "Watch and buy in-room",
    icon: Radio,
    kicker: "Shop",
    headline: "Watch the room. Buy the item.",
    lede: "Sellers go live from their shop. You watch, ask, and buy the piece they are showing without leaving the room.",
    points: [
      "Open rooms are listed on Live.",
      "The item in the room is the one you can buy.",
      "Sign up to join with your own account.",
    ],
    openTo: "/live",
    openLabel: "Watch live",
    registerRole: "buyer",
  },
  {
    slug: "crosslist",
    group: "Sell",
    title: "Crosslist",
    blurb: "One listing, many shops",
    icon: Store,
    kicker: "Sell",
    headline: "Write it once. List it in many shops.",
    lede: "A seller account drafts one listing and sends it to the marketplaces you connect, including eBay, Etsy, Poshmark, and Mercari.",
    points: [
      "One draft holds the title, price, and photos.",
      "Each connected shop gets that listing.",
      "Sign up as a seller to open crosslist.",
    ],
    openTo: "/seller/crosslist",
    openLabel: "Open crosslist",
    registerRole: "seller",
  },
  {
    slug: "auto-delist",
    group: "Sell",
    title: "Auto-delist",
    blurb: "Never double-sell",
    icon: BellOff,
    kicker: "Sell",
    headline: "One sale takes the other listings down.",
    lede: "When a piece sells on one marketplace, Shoptosa pulls it from the others so you do not sell the same item twice.",
    points: [
      "Sale detection watches the channels you connected.",
      "The sold channel stays. The rest come down.",
      "Sign up as a seller to turn automations on.",
    ],
    openTo: "/seller/automations",
    openLabel: "Open automations",
    registerRole: "seller",
  },
  {
    slug: "ai-photoshoots",
    group: "Sell",
    title: "AI photoshoots",
    blurb: "Studio shots from a flat lay",
    icon: Camera,
    kicker: "Sell",
    headline: "A flat lay becomes a studio shot.",
    lede: "Start from the photo you already have. Photoshoot builds a listing image you can use on Shoptosa and on the channels you crosslist to.",
    points: [
      "Upload the product photo you have.",
      "The result is a shot meant for a listing.",
      "Sign up as a seller to use photoshoot.",
    ],
    openTo: "/seller/photoshoot",
    openLabel: "Open photoshoot",
    registerRole: "seller",
  },
  {
    slug: "marketplaces",
    group: "Sell",
    title: "Marketplaces",
    blurb: "eBay, Etsy, Poshmark…",
    icon: Link2,
    kicker: "Sell",
    headline: "Connect the shops you already sell on.",
    lede: "Marketplaces is where a seller links eBay, Etsy, Poshmark, and the other channels crosslist and auto-delist use.",
    points: [
      "Each shop connects on its own.",
      "A connected shop can receive listings and report a sale.",
      "Sign up as a seller to connect a marketplace.",
    ],
    openTo: "/seller/connections",
    openLabel: "Open marketplaces",
    registerRole: "seller",
  },
  {
    slug: "graph-api",
    group: "Agents",
    title: "Graph API",
    blurb: "Build shopping agents, earn on sales",
    icon: Bot,
    kicker: "Agents",
    headline: "Give an agent a catalog and a buy link.",
    lede: "The developer API searches the product graph and returns offers with a tracked buy link. A sale through that link is credited to your key.",
    points: [
      "Search, product, and click routes live under the developer docs.",
      "Each key records the clicks and sales it sends.",
      "Sign up as a developer to create a key.",
    ],
    openTo: "/developers",
    openLabel: "Open the API",
    registerRole: "developer",
  },
  {
    slug: "affiliate-rails",
    group: "Agents",
    title: "Affiliate rails",
    blurb: "Rakuten, CJ, Skimlinks",
    icon: Link2,
    kicker: "Agents",
    headline: "Outbound buys stay attributed.",
    lede: "Partner catalogs already carry a network tag. When a shopper leaves through that link, the network records the click for the publisher account that is connected.",
    points: [
      "eBay, Awin, Rakuten, CJ, Skimlinks, and the other connected networks keep their own tags.",
      "A click pays only after that network has approved the store.",
      "Sign up to use the shop, or as a developer to send traffic through the API.",
    ],
    openTo: "/developers",
    openLabel: "See the developer API",
    registerRole: "developer",
  },
  {
    slug: "product-graph",
    group: "Agents",
    title: "Product graph",
    blurb: "One item, many offers",
    icon: Layers,
    kicker: "Agents",
    headline: "One product. Every offer beside it.",
    lede: "The graph treats a product as one item and hangs each store’s price, photo, and buy link on it. Shoppers and agents read the same offers.",
    points: [
      "Offers stay attached to the store that priced them.",
      "Search and the API return those offers together.",
      "Sign up as a developer to query the graph, or shop as a buyer.",
    ],
    openTo: "/developers",
    openLabel: "Read the API",
    registerRole: "developer",
  },
  {
    slug: "all-capabilities",
    group: "Agents",
    title: "All capabilities",
    blurb: "Every feature in one place",
    icon: LayoutGrid,
    kicker: "Shoptosa",
    headline: "Shop, sell, and build on one graph.",
    lede: "Each feature has its own page. Sign up or log in from any of them, then open the tool that matches the account you create.",
    points: [
      "Shoppers compare, search, try on, and watch live rooms.",
      "Sellers crosslist, delist on a sale, shoot photos, and connect marketplaces.",
      "Developers query the graph and earn when a tracked buy link converts.",
    ],
    openTo: "/shop",
    openLabel: "Open the shop",
    registerRole: "buyer",
  },
];

export function featureBySlug(slug) {
  return FEATURES.find((item) => item.slug === slug) || null;
}

export function registerHref(role) {
  return `/auth?mode=register&role=${role || "buyer"}`;
}
